import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import EditIcon from '../../assets/img/edit-shape-icon.png'
import InfoIcon from '../../assets/img/svg/info-icon.svg'
import {
  // checkIfPastDate,
  convertTimestampToDateWithTimezone,
  convertTimestampToTimeWithTimezone,
  dayjsToWallClock,
  formatAmount,
  formatBookingForDisplay,
  getBookingDayjs,
  getISOToUTCDateFromTimestamp,
  getTimeStamp,
  getUserRole,
  getUserTimezone,
  hasAccess,
  isDateBefore
} from '../../helper/function'
import { cancelPropertyScheduleRequest, editManualPropertySchedule, getSchedulePropertyDataById } from '../../redux/slices/propertySlice'
import { SpinnerContext } from '../../context/spinnerContext'
import { errorToast, successToast } from '../../util/Toast/toast'
import Select from 'react-select'
import { getAllInviteCleaner, inviteCleaner } from '../../redux/slices/cleanersSlice'
import ConfirmationModal from '../confirmationModal'
import { yupResolver } from '@hookform/resolvers/yup'
import { editScheduleClening } from '../../util/validationSchema'
import DeletePopup from '../deletePopup'
import CommonDateTimePicker from '../commonDateTimePicker'
import { OverlayTrigger, Popover } from 'react-bootstrap'

const ScheduledModalContent = ({ data, setIsRefresh, onClose, handlePagination, page, selectedTimezone = null }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const { changeLoader } = useContext(SpinnerContext)
  const { allInviteCleaner } = useSelector(({ cleaners }) => cleaners)
  const { scheduledProperties } = useSelector(({ property }) => property)
  const [toggleModal, setToggleModal] = useState(null)

  // Restrição removida - permite edição em todos os status
  const isInNotProgress = true

  const [isOpenCancelCleaning, setIsOpenCancelCleaning] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const openDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen)

  const toggleCancelCleaning = () => {
    setIsOpenCancelCleaning(!isOpenCancelCleaning)
  }

  const onCancelCleaningClick = () => {
    if (data?.is_accepted && shouldApplyCancellationFee(data?.booking_date_time)) {
      toggleCancelCleaning()
    } else {
      openDeleteModal()
    }
  }

  const shouldApplyCancellationFee = (scheduledTimestamp) => {
    const scheduledTime = scheduledTimestamp * 1000
    const currentTime = Date.now()
    const twoHoursBefore = scheduledTime - 2 * 60 * 60 * 1000
    return currentTime >= twoHoursBefore
  }

  const { properties, next_checkin_time, booking_date_time, cleaners_id, property_schedule_id, property_timezone } = data

  // Genuine property timezone (no user fallback) — used for the timezone-aware picker / wall clock
  const propertyTimezone = property_timezone || properties?.timezone || null

  // Use selected timezone if provided and not 'property', otherwise use property's timezone
  const propertyOwnTimezone = property_timezone || properties?.timezone || getUserTimezone()
  const timezoneValue = selectedTimezone?.value || selectedTimezone
  const displayTimezone = timezoneValue && timezoneValue !== 'property' ? timezoneValue : propertyOwnTimezone
  const isUsingDifferentTimezone = timezoneValue && timezoneValue !== 'property' && timezoneValue !== propertyOwnTimezone

  const cleanerName = useMemo(() => (cleaners_id ? `${cleaners_id.first_name || ''} ${cleaners_id?.last_name || ''}` : ''), [cleaners_id])

  const viewLogVisiable = useMemo(() => !location?.pathname?.includes('view-logs'), [location])

  const _editAccess = hasAccess('noEditAccess', getUserRole())

  const { control: cleanerControl, handleSubmit: cleanerHandleSubmit } = useForm({
    defaultValues: {
      cleaners_id: scheduledProperties?.getPropertyScheduleData?.cleaners // Default value for cleaner select
    }
  })
  useEffect(() => {
    const fetchData = async () => {
      let payload = {
        property_schedule_id: property_schedule_id
      }
      try {
        dispatch(getAllInviteCleaner(payload))
      } catch (err) {
        console.error('Error fetching cleaners:', err)
      }
    }
    fetchData()
  }, [property_schedule_id])

  const updatedValues = useMemo(
    () => ({
      booking_date_time: booking_date_time ? (getBookingDayjs(data, propertyTimezone) || getISOToUTCDateFromTimestamp(booking_date_time)) : null,
      next_check_in: next_checkin_time ? getISOToUTCDateFromTimestamp(next_checkin_time) : null
    }),
    [booking_date_time, next_checkin_time, propertyTimezone]
  )

  const { control, trigger, handleSubmit, setValue, watch } = useForm({
    defaultValues: { ...updatedValues, name: cleanerName },
    resolver: yupResolver(editScheduleClening)
  })

  const onSubmit = async (value) => {
    const { booking_date_time, next_check_in } = value
    const payload = {
      data: {
        booking_date_time: booking_date_time ? getTimeStamp(booking_date_time) : '',
        booking_wall_clock: booking_date_time ? dayjsToWallClock(booking_date_time, propertyTimezone) : '',
        next_checkin_time: next_check_in ? getTimeStamp(next_check_in) : ''
      },
      scheduleID: property_schedule_id
    }

    changeLoader(true)
    dispatch(editManualPropertySchedule(payload))
      .then((response) => {
        if (response?.payload?.data?.status_code === 200) {
          successToast(response?.payload?.data?.status_message || 'Success!')
          changeLoader(false)
          getPropertyData(property_schedule_id)
          setIsRefresh && setIsRefresh(true)
        } else {
          errorToast(response?.payload?.data?.status_message || 'Failed!')
          changeLoader(false)
        }
      })
      .catch((err) => {
        errorToast(err?.status_message || 'Failed!')
      })
  }

  const onRedirectViewLog = useCallback(() => {
    onClose()
    navigate(`/view-logs/${property_schedule_id}`)
  }, [navigate, onClose])

  const onInvite = useCallback(
    async (value) => {
      if (!value?.cleaners_id) {
        errorToast('Please select a cleaner first.')
        return
      }
      const data = {
        id: property_schedule_id,
        data: { cleaners_id: value?.cleaners_id?.cleaners_id }
      }

      changeLoader(true)
      dispatch(inviteCleaner(data))
        .then((response) => {
          if (response?.payload?.status === 200) {
            successToast(response?.payload?.data?.status_message || 'Success!')
            setIsRefresh && setIsRefresh(true)
            onClose()
          } else {
            errorToast(response?.payload?.data?.status_message || 'Failed!')
          }
          changeLoader(false)
        })
        .catch((err) => {
          console.error('Error inviting cleaner:', err)
          changeLoader(false)
        })
    },
    [property_schedule_id, setIsRefresh, onClose]
  )

  const getPropertyData = async (schedule_id) => {
    changeLoader(true)
    try {
      dispatch(getSchedulePropertyDataById(schedule_id))
      setToggleModal((prev) => !prev)
    } catch (err) {
      console.error('Error fetching property data:', err)
    } finally {
      changeLoader(false)
    }
  }

  const onCancelCleaning = async (cancellation_charge) => {
    const payload = {
      property_id: property_schedule_id,
      cancellation_charge: cancellation_charge
    }
    changeLoader(true)
    dispatch(cancelPropertyScheduleRequest(payload))
      .then((response) => {
        if (response?.payload?.status === 200) {
          successToast(response?.payload?.data?.status_message || 'Success!')
          if (setIsRefresh) setIsRefresh(true)
          if (handlePagination) handlePagination(1)
          onClose()
        } else {
          errorToast(response?.payload?.data?.status_message || 'Failed!')
        }
      })
      .catch((err) => {
        errorToast(err?.message || 'Failed!')
      })
      .finally(() => {
        changeLoader(false)
        setIsOpenCancelCleaning(false)
      })
  }
  const onCompleteCleaning = async () => {
    if (!scheduledProperties?.getPropertyScheduleData?.cleaners) {
      errorToast('Cleaning cannot be completed as no cleaner has been assigned yet. Please assign a cleaner first to proceed.')
      return
    }
    if (!scheduledProperties?.getPropertyScheduleData?.is_accepted) {
      errorToast('Cleaning cannot be completed as cleaner has not accepted cleaning.')
      return
    }
    onClose()
    navigate(`/${page}/complete-cleaning/${property_schedule_id}`)
  }

  // const overFun = useMemo(() => {
  //   return next_checkin_time
  //     ? checkIfPastDate(next_checkin_time, false)
  //     : checkIfPastDate(booking_date_time, true)
  // }, [booking_date_time,next_checkin_time])

  const overFun = false

  const popoverClick = useMemo(
    () => (
      <Popover id="popover-trigger-click">
        <div className="popover-content-custom p-3">
          <div className="popover-description mb-0">
            You can only modify the checkout time. To change the check-in time, select the same date as checkout and adjust the time accordingly
          </div>
        </div>
      </Popover>
    ),
    []
  )
  const bookingDate = watch('booking_date_time')

  const oneDayBefore = useMemo(
    () => (bookingDate ? new Date(new Date(bookingDate).setDate(new Date(bookingDate).getDate() - 1)) : null),
    [bookingDate]
  )

  return (
    <div className="modal-cancel">
      <div className="common_card_div status-div position-relative">
        <div className="input_btn_group_div">
          {!overFun && _editAccess && (
            <form onSubmit={cleanerHandleSubmit(onInvite)}>
              <Controller
                name="cleaners_id"
                control={cleanerControl}
                defaultValue={null} // Set defaultValue to null for react-select
                render={({ field }) => (
                  <Select
                    {...field}
                    className="react-selectcomponent form-control select-box-filed border-remove p-0"
                    getOptionLabel={(option) => `${option?.first_name || ''} ${option?.last_name || ''}`}
                    getOptionValue={(option) => `${option?.cleaners_id}`}
                    options={allInviteCleaner || []}
                    isSearchable={true}
                    placeholder="Select Main Cleaner"
                    classNamePrefix="select_kanakku"
                    isDisabled={!isInNotProgress}
                  />
                )}
              />
              <button type="submit" className="btn btn-primary" disabled={!isInNotProgress}>
                Invite Cleaner
              </button>
            </form>
          )}
        </div>
        {toggleModal ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group">
              <div className="d-flex gap-2">
                <label>Checkout:</label>
                <OverlayTrigger trigger={['hover', 'focus']} placement="auto" overlay={popoverClick} rootClose>
                  <span>
                    <img src={InfoIcon} alt="Info" width={20} />
                  </span>
                </OverlayTrigger>
              </div>

              <div className="date_picker_main_div">
                <CommonDateTimePicker
                  name="booking_date_time"
                  control={control}
                  placeholder="yyyy-MM-ddTHH:mm A"
                  onDateChange={() => {
                    trigger('booking_date_time')
                  }}
                  disablePast={false}
                  propertyTimezone={propertyTimezone}
                />
              </div>
              {propertyTimezone && (
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  Horário local da propriedade — {properties?.city || properties?.address || ''} ({propertyTimezone.replace('_', ' ')})
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Next Check-In:</label>
              <div className="date_picker_main_div">
                <CommonDateTimePicker
                  name="next_check_in"
                  control={control}
                  placeholder="yyyy-MM-ddTHH:mm A"
                  onDateChange={() => {
                    trigger('next_check_in')
                  }}
                  disablePast={false}
                  shouldDisableDate={(date) => isDateBefore(date, oneDayBefore)}
                  propertyTimezone={propertyTimezone}
                />
              </div>
            </div>
            <div className="multi_btn_div mt-2">
              <button
                type="button"
                className="btn common-outline-btn w-100"
                onClick={() => {
                  setValue('booking_date_time', updatedValues.booking_date_time)
                  setValue('next_check_in', updatedValues.next_check_in)

                  setToggleModal(!toggleModal)
                }}
              >
                Close
              </button>
              <button type="submit" className="btn btn-primary common-field-btn w-100">
                Update
              </button>
            </div>
          </form>
        ) : (
          <>
            <h6 className={`mb-2 ${!overFun ? '' : 'check-time-over'}`}>
              Checkout:{' '}
              {booking_date_time ? (
                isUsingDifferentTimezone ? (
                  <>
                    <span>{convertTimestampToDateWithTimezone(booking_date_time, displayTimezone)}</span> <span>-</span> <span>{convertTimestampToTimeWithTimezone(booking_date_time, displayTimezone)}</span>
                  </>
                ) : (
                  <span>{formatBookingForDisplay(data, propertyTimezone)}</span>
                )
              ) : (
                '--'
              )}
            </h6>
            <h6 className={`mb-2 ${!overFun ? '' : 'check-time-over'}`}>
              Next Check-In:{' '}
              {next_checkin_time ? (
                <>
                  <span>{convertTimestampToDateWithTimezone(next_checkin_time, displayTimezone)}</span> - <span>{convertTimestampToTimeWithTimezone(next_checkin_time, displayTimezone)}</span>
                </>
              ) : (
                '--'
              )}
            </h6>
            {isUsingDifferentTimezone && (
              <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                <i className="fa-solid fa-globe me-1"></i>
                Property timezone ({propertyOwnTimezone}): {booking_date_time ? `${convertTimestampToDateWithTimezone(booking_date_time, propertyOwnTimezone)} - ${convertTimestampToTimeWithTimezone(booking_date_time, propertyOwnTimezone)}` : '--'}
                {next_checkin_time ? ` / ${convertTimestampToDateWithTimezone(next_checkin_time, propertyOwnTimezone)} - ${convertTimestampToTimeWithTimezone(next_checkin_time, propertyOwnTimezone)}` : ''}
              </p>
            )}
            {!overFun && isInNotProgress && _editAccess && (
              <div className="edit_div" onClick={() => setToggleModal(!toggleModal)}>
                <img className="img-fluid" src={EditIcon} alt="Image" />
              </div>
            )}
          </>
        )}
      </div>

      <div className="common_card_div status-div">
        <h6 className="mb-2">{properties?.address}</h6>
        <h6 className="mb-2">
          {properties?.get_in}: <span>{properties?.get_in_instruction}</span>
        </h6>
        <h6 className="mb-2">
          Rooms: <span>{properties?.number_of_rooms || 0}</span>
        </h6>
        <h6 className="mb-2">
          Bathrooms: <span>{properties?.number_of_bathroom || 0}</span>
        </h6>
        {hasAccess('accessFinalAmount', getUserRole()) && (
          <h6 className="mb-0">
            Value: <span>{formatAmount(properties?.total_value) || 0}</span>
          </h6>
        )}
      </div>
      {!overFun && _editAccess && (
        <div className="multi_btn_div modal-button-group">
          <button type="button" className="btn btn-primary common-field-btn red_btn w-100" onClick={() => onCancelCleaningClick()}>
            Cancel Cleaning
          </button>
          <button type="button" className="btn btn-success common-field-btn complete w-100" onClick={() => onCompleteCleaning()}>
            Complete Cleaning
          </button>
          {viewLogVisiable && (
            <button type="button" className="btn btn-primary common-field-btn sky_btn w-100" onClick={() => onRedirectViewLog()}>
              View Log
            </button>
          )}
        </div>
      )}
      {viewLogVisiable && !_editAccess && (
        <div className="multi_btn_div modal-button-group">
          <button type="submit" className="btn btn-primary common-field-btn sky_btn" onClick={() => onRedirectViewLog()}>
            View Log
          </button>
        </div>
      )}
      <ConfirmationModal
        onCancelCleaning={onCancelCleaning}
        isOpenCancelCleaning={isOpenCancelCleaning}
        toggleCancelCleaning={toggleCancelCleaning}
      />
      {isDeleteModalOpen && (
        <DeletePopup isDeleteModalOpen={isDeleteModalOpen} toggle={openDeleteModal}>
          <div className="multi_btn_div">
            <button type="button" className="btn common-outline-btn w-100" onClick={() => setIsDeleteModalOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger common-field-btn w-100" onClick={() => onCancelCleaning(false)}>
              Delete
            </button>
          </div>
        </DeletePopup>
      )}
    </div>
  )
}
export default ScheduledModalContent
